import { useEffect, useState } from "react";
import { stockAPI } from "../../lib/api";
import { Link } from "react-router-dom";
import { ArrowUpDown, Filter, Plus } from "lucide-react";
import { toast } from "sonner";
import { Header } from '../../components/layout/Header';

const TYPE_META = {
    entrada: { label: "Entrada", color: "text-green-400", dot: "bg-green-500" },
    saida:   { label: "Saída",   color: "text-red-400",   dot: "bg-red-500" },
    ajuste:  { label: "Ajuste",  color: "text-blue-400",  dot: "bg-blue-500" },
};

function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function StockMovements() {
    const [movements, setMovements] = useState([]);
    const [total, setTotal] = useState(0);
    const [products, setProducts] = useState([]);
    const [productId, setProductId] = useState("");
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const LIMIT = 30;

    const load = async () => {
        setLoading(true);
        try {
            const params = { skip: page * LIMIT, limit: LIMIT };
            if (productId) params.product_id = productId;
            const [m, p] = await Promise.all([stockAPI.movements(params), stockAPI.products()]);
            setMovements(m.data?.movements || []);
            setTotal(m.data?.total || 0);
            setProducts(p.data || []);
        } catch { toast.error("Erro ao carregar"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [page, productId]);

    return (
        <>
        <Header title="Movimentações" subtitle={`${total} movimentações registradas`} actions={<><select value={productId} onChange={e => { setProductId(e.target.value); setPage(0); }} className="bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-stone-300 focus:outline-none"><option value="">Todos os produtos</option>{products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select><Link to="/estoque/entrada" className="flex items-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm px-4 py-2 rounded-lg transition-colors"><Plus className="w-4 h-4" /> Nova entrada</Link></>} />
        <div className="p-6 space-y-5">

            <div className="bg-stone-900 border border-stone-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-stone-800">
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Tipo</th>
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Produto</th>
                            <th className="text-right px-4 py-3 text-xs text-stone-500 font-medium">Qtd</th>
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Motivo</th>
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Comprador</th>
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Data</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-800/50">
                        {loading ? (
                            <tr><td colSpan={6} className="text-center py-12 text-stone-500 text-xs">Carregando...</td></tr>
                        ) : movements.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-12 text-stone-500 text-xs">Nenhuma movimentação</td></tr>
                        ) : movements.map(m => {
                            const meta = TYPE_META[m.type] || TYPE_META.ajuste;
                            const product = products.find(p => p.id === m.product_id);
                            return (
                                <tr key={m.id} className="hover:bg-stone-800/30 transition-colors">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${meta.dot}`} />
                                            <span className={`text-xs font-medium ${meta.color}`}>{meta.label}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-stone-300">{product?.name || m.product_id}</td>
                                    <td className="px-4 py-3 text-right">
                                        <span className={`text-sm font-bold ${meta.color}`}>
                                            {m.type === "entrada" ? "+" : m.type === "saida" ? "-" : "~"}{m.quantity}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-stone-400">{m.reason || "—"}</td>
                                    <td className="px-4 py-3 text-xs text-stone-400">{m.buyer_name || "—"}</td>
                                    <td className="px-4 py-3 text-xs text-stone-500">{fmtDate(m.created_at)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {total > LIMIT && (
                    <div className="border-t border-stone-800 px-4 py-3 flex items-center justify-between">
                        <span className="text-xs text-stone-500">{page * LIMIT + 1}–{Math.min((page+1)*LIMIT, total)} de {total}</span>
                        <div className="flex gap-2">
                            <button disabled={page === 0} onClick={() => setPage(p => p-1)}
                                className="px-3 py-1 text-xs border border-stone-700 rounded-lg text-stone-400 hover:text-stone-200 disabled:opacity-30 transition-colors">Anterior</button>
                            <button disabled={(page+1)*LIMIT >= total} onClick={() => setPage(p => p+1)}
                                className="px-3 py-1 text-xs border border-stone-700 rounded-lg text-stone-400 hover:text-stone-200 disabled:opacity-30 transition-colors">Próxima</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
        </>
    );
}
